import { type Chat, type ChatMessage, groupAlbums, isVote, previewOf } from "@rhema/client";
import {
  Avatar,
  ClockIcon,
  cn,
  DayDivider,
  DEFAULT_REACTIONS,
  EncryptionNotice,
  focusRing,
  GroupedMessage,
  HandIcon,
  MessageBubble,
  type MessageBubbleProps,
  type Reaction,
  SystemLine,
  TypingBubble,
} from "@rhema/ui";
import { Fragment, type ReactNode, useState } from "react";
import { MessageBoundary } from "../app/Crash";
import { clockTime, dayKey, dayLabel, firstName, toneFor } from "../data/view";
import { ENCRYPTION_NOTICE, EncryptionSheet } from "./EncryptionSheet";
import { ImportedHistory, importRuns, isImportBatch, runKey } from "./ImportedHistory";
import { albumParts, contentParts } from "./MessageContent";
import { formatOf, MessageText, plainOf, type RichRender, useRichRender } from "./rich";

export const WAITING =
  "Waiting for this message. It appears when the sender's device or another of your devices is online.";
export const LOST =
  "This message can't be recovered. The device that sent it signed out before its key was backed up.";
export const DELETED = "This message was deleted.";
export const UNREADABLE = "This message could not be verified, so it is hidden.";
export const MALFORMED = "This message could not be shown.";

/** Whether the message has content someone can act on: reply, react, copy, edit or delete. */
export function isReadable(m: ChatMessage): boolean {
  return m.kind === "message" && !m.deleted && m.content !== null;
}

/** One line of text for a message, used in the actions overlay, the reply bar and quotes. */
export function textOf(m: ChatMessage): string {
  if (m.deleted || m.problem === "deleted") return DELETED;
  if (!m.content) {
    if (m.problem === "missing_key") return WAITING;
    if (m.problem === "malformed") return MALFORMED;
    return m.problem === "lost" ? LOST : UNREADABLE;
  }
  // Formatting marks have no place in one line of text, so the words show without them.
  if (m.content.type === "text") return plainOf(m.edited ?? m.content.body, formatOf(m));
  return previewOf(m.content);
}

export function nameIn(chat: Chat, userId: string | null, meId: string | undefined): string {
  if (!userId) return "Someone";
  if (userId === meId) return "You";
  return chat.members.find((m) => m.user.id === userId)?.user.display_name ?? "Someone";
}

function reactionsOf(m: ChatMessage, meId: string | undefined): Reaction[] {
  return Object.entries(m.reactions).map(([emoji, users]) => ({
    emoji,
    count: users.length,
    mine: meId ? users.includes(meId) : false,
    label: DEFAULT_REACTIONS.find((r) => r.emoji === emoji)?.label,
  }));
}

function Muted({ children }: { children: ReactNode }) {
  return <span className="text-ink-meta italic">{children}</span>;
}

function bodyOf(m: ChatMessage, chat: Chat, render: RichRender): ReactNode {
  if (!isReadable(m)) return <Muted>{textOf(m)}</Muted>;
  if (m.content?.type === "text") {
    const text = <MessageText m={m} chat={chat} render={render} />;
    const workflow = m.content.workflow;
    if (!workflow) return text;
    // A message a space workflow posted says so, with the workflow's name.
    const Icon = workflow.kind === "welcome" ? HandIcon : ClockIcon;
    return (
      <>
        <span className="mb-1 flex items-center gap-1.5 text-[12.5px] font-semibold text-ink-secondary select-none">
          <Icon size={14} strokeWidth={2.2} />
          {workflow.name || (workflow.kind === "welcome" ? "Welcome message" : "Workflow")}
        </span>
        {text}
      </>
    );
  }
  return textOf(m);
}

type Props = {
  chat: Chat;
  messages: ChatMessage[];
  meId: string | undefined;
  hasOlder: boolean;
  typing: string[];
  describe: (system: Record<string, string>) => string;
  onRetry: (m: ChatMessage) => void;
  /** Where the safety numbers can be checked, offered from the encryption sheet. */
  onNotice?: () => void;
  actionProps: (id: string) => Partial<MessageBubbleProps>;
  /** Opens a message's thread. Where it is given, messages with replies show how many. */
  onThread?: (m: ChatMessage) => void;
  /** The message a link opened, picked out for a moment. */
  highlightId?: string | null;
  /** The ids of the messages you starred here. */
  starred?: Set<string>;
  /** Desktop: the hover bar for a message. */
  toolbar?: (m: ChatMessage) => ReactNode;
};

/** "3 replies, last at 10:42" under a message with a thread. */
function ThreadSummary({
  m,
  own,
  indent,
  onOpen,
}: {
  m: ChatMessage;
  own: boolean;
  indent: boolean;
  onOpen: () => void;
}) {
  const count = m.replyCount ?? 0;
  return (
    <button
      type="button"
      onClick={onOpen}
      className={cn(
        "mt-1 mb-1 flex cursor-pointer items-center gap-1.5 rounded-full border-0 bg-transparent px-2 py-0.5 text-[13px] font-semibold text-blue hover:bg-blue-tint",
        own ? "self-end" : "self-start",
        indent && "ml-9",
        focusRing,
      )}
    >
      {count === 1 ? "1 reply" : `${count} replies`}
      {m.lastReplyAt && <span className="font-normal text-ink-meta">last at {clockTime(m.lastReplyAt)}</span>}
    </button>
  );
}

type RowProps = {
  m: ChatMessage;
  chat: Chat;
  meId: string | undefined;
  own: boolean;
  group: boolean;
  newRun: boolean;
  spacing: string;
  byId: Map<string, ChatMessage>;
  onRetry: (m: ChatMessage) => void;
  actionProps: (id: string) => Partial<MessageBubbleProps>;
  highlightId?: string | null;
  starred?: Set<string>;
  toolbar?: (m: ChatMessage) => ReactNode;
  /** Photos and videos sent together, drawn as one bubble, in album order. */
  album?: ChatMessage[];
  render: RichRender;
};

const DELIVERY_ORDER = ["failed", "sending", "sent", "delivered", "read"] as const;

/** An album is only as far along as its slowest photo. */
function albumStatus(items: ChatMessage[]): ChatMessage["status"] {
  let lowest = DELIVERY_ORDER.length - 1;
  for (const m of items) lowest = Math.min(lowest, DELIVERY_ORDER.indexOf(m.status));
  return DELIVERY_ORDER[Math.max(0, lowest)] ?? "sent";
}

function mergedReactions(items: ChatMessage[], meId: string | undefined): Reaction[] {
  const byEmoji = new Map<string, Reaction>();
  for (const m of items)
    for (const r of reactionsOf(m, meId)) {
      const known = byEmoji.get(r.emoji);
      byEmoji.set(
        r.emoji,
        known ? { ...known, count: known.count + r.count, mine: known.mine || r.mine } : r,
      );
    }
  return [...byEmoji.values()];
}

/** A file you are sending shows the file and what went wrong, in place of the plain failed bubble. */
function failsInPlace(m: ChatMessage): boolean {
  return m.content?.type === "media" && m.content.kind !== "voice";
}

function AlbumBubble({
  items,
  chat,
  meId,
  own,
  group,
  newRun,
  spacing,
  byId,
  actionProps,
  highlightId,
  starred,
  toolbar,
}: RowProps & { items: ChatMessage[] }) {
  const anchor = items[0] as ChatMessage;
  const sender = chat.members.find((x) => x.user.id === anchor.senderId)?.user;
  const replyTo = items
    .map((x) => (x.content?.type === "media" ? x.content.reply_to : undefined))
    .find(Boolean);
  const quoted = replyTo ? byId.get(replyTo) : undefined;
  const reply = replyTo
    ? quoted
      ? { author: nameIn(chat, quoted.senderId, meId), text: textOf(quoted) }
      : { author: "Earlier message", text: "Scroll up to load it." }
    : undefined;
  const sent = (x: ChatMessage) => x.seq > 0 && isReadable(x);
  const parts = albumParts(items, (x) => (sent(x) ? actionProps(x.id) : undefined));
  const status = albumStatus(items);
  return (
    <MessageBubble
      {...parts}
      {...(sent(anchor) ? actionProps(anchor.id) : {})}
      data-message-id={anchor.id}
      forwarded={anchor.content?.type === "media" && anchor.content.forwarded === true}
      starred={items.some((x) => starred?.has(x.id))}
      highlighted={items.some((x) => x.id === highlightId)}
      toolbar={sent(anchor) ? toolbar?.(anchor) : undefined}
      direction={own ? "out" : "in"}
      time={clockTime(items[items.length - 1]?.createdAt ?? anchor.createdAt)}
      status={own ? status : undefined}
      statusLabel={own && group && status === "read" ? "Read by everyone" : undefined}
      edited={items.some((x) => x.edited !== null)}
      reply={reply}
      reactions={mergedReactions(items, meId)}
      className={group ? undefined : spacing}
      author={
        group && !own && newRun && sender
          ? {
              name: chat.guests?.includes(sender.id)
                ? `${firstName(sender.display_name)} (Guest)`
                : firstName(sender.display_name),
              tone: toneFor(sender.id),
            }
          : undefined
      }
    >
      {parts.children}
    </MessageBubble>
  );
}

/** One message's bubble, drawn as its own component so a failure stays inside its boundary. */
function RowBubble(props: RowProps) {
  const {
    m,
    chat,
    meId,
    own,
    group,
    newRun,
    spacing,
    byId,
    onRetry,
    actionProps,
    highlightId,
    starred,
    toolbar,
    album,
    render,
  } = props;
  const sender = chat.members.find((x) => x.user.id === m.senderId)?.user;
  const time = clockTime(m.createdAt);
  const readable = isReadable(m);

  if (album) return <AlbumBubble {...props} items={album} />;
  if (own && m.status === "failed" && m.seq === 0 && !failsInPlace(m)) {
    return (
      <MessageBubble
        direction="out"
        time={time}
        variant="failed"
        onRetry={() => onRetry(m)}
        className={group ? undefined : spacing}
      >
        {textOf(m)}
      </MessageBubble>
    );
  }
  const content = m.content;
  let reply: MessageBubbleProps["reply"];
  if ((content?.type === "text" || content?.type === "media") && content.reply_to) {
    const quoted = byId.get(content.reply_to);
    reply = quoted
      ? { author: nameIn(chat, quoted.senderId, meId), text: textOf(quoted) }
      : { author: "Earlier message", text: "Scroll up to load it." };
  }
  const parts = readable ? contentParts(m, time) : null;
  const body = parts && content?.type !== "text" ? parts.children : bodyOf(m, chat, render);
  return (
    <MessageBubble
      {...(parts ?? {})}
      {...(readable && m.seq > 0 ? actionProps(m.id) : {})}
      data-message-id={m.id}
      forwarded={
        readable && (content?.type === "text" || content?.type === "media") && content.forwarded === true
      }
      starred={readable && Boolean(starred?.has(m.id))}
      highlighted={highlightId === m.id}
      toolbar={readable && m.seq > 0 ? toolbar?.(m) : undefined}
      direction={own ? "out" : "in"}
      time={time}
      status={own && readable ? m.status : undefined}
      statusLabel={own && group && m.status === "read" ? "Read by everyone" : undefined}
      edited={readable && m.edited !== null}
      reply={readable ? reply : undefined}
      reactions={readable ? reactionsOf(m, meId) : undefined}
      className={group ? undefined : spacing}
      author={
        group && !own && newRun && sender
          ? {
              // Guests in a channel are marked, as Slack marks them.
              name: chat.guests?.includes(sender.id)
                ? `${firstName(sender.display_name)} (Guest)`
                : firstName(sender.display_name),
              tone: toneFor(sender.id),
            }
          : undefined
      }
    >
      {body}
    </MessageBubble>
  );
}

export function MessageList({
  chat,
  messages: all,
  meId,
  hasOlder,
  typing,
  describe,
  onRetry,
  onNotice,
  actionProps,
  onThread,
  highlightId,
  starred,
  toolbar,
}: Props) {
  // A channel is drawn as a group is: names and avatars beside other people's messages.
  const group = chat.kind === "group" || chat.kind === "channel";
  const byId = new Map(all.map((m) => [m.id, m]));
  // Votes only change the poll they belong to, so they have no place of their own in the timeline.
  const messages = all.filter((m) => !isVote(m));
  // Photos and videos sent together draw as one album, where the first of them sits.
  const albums = new Map<string, ChatMessage[]>();
  const inAlbum = new Set<string>();
  for (const g of groupAlbums(messages)) {
    if (g.kind !== "album") continue;
    const first = messages.find((m) => g.messages.includes(m));
    if (!first) continue;
    albums.set(first.id, g.messages);
    for (const m of g.messages) if (m !== first) inAlbum.add(m.id);
  }
  const items: ReactNode[] = [];
  const [explained, setExplained] = useState(false);
  const render = useRichRender(chat);

  if (!hasOlder) {
    items.push(
      <EncryptionNotice key="notice" onClick={() => setExplained(true)}>
        {ENCRYPTION_NOTICE}
      </EncryptionNotice>,
    );
    if (chat.disappear_after) {
      items.push(
        <SystemLine key="disappearing">
          New messages disappear {disappearLabel(chat.disappear_after)} after they are sent.
        </SystemLine>,
      );
    }
  }

  // Imported WhatsApp history is drawn as one run per import, where its first batch sits.
  const runs = importRuns(messages);

  let lastDay = "";
  messages.forEach((m, i) => {
    if (isImportBatch(m)) {
      const run = runs.get(runKey(m));
      if (run && run.anchorId === m.id) {
        items.push(
          <MessageBoundary key={m.id} own={false}>
            <ImportedHistory run={run} chat={chat} meId={meId} />
          </MessageBoundary>,
        );
        // The next live message starts with its own day.
        lastDay = "";
      }
      return;
    }
    if (inAlbum.has(m.id)) return;
    const day = dayKey(m.createdAt);
    if (day !== lastDay) {
      lastDay = day;
      items.push(<DayDivider key={`day-${day}`}>{dayLabel(m.createdAt)}</DayDivider>);
    }

    if (m.kind === "system") {
      const text = describe(m.system ?? {});
      if (text) items.push(<SystemLine key={m.id}>{text}</SystemLine>);
      return;
    }
    // The name comes from who sent the notice, never from its text, so no one can speak for another.
    if (m.content?.type === "notice" && !m.deleted) {
      const who = m.senderId === meId ? "You" : firstName(nameIn(chat, m.senderId, meId));
      items.push(
        <SystemLine key={m.id}>
          {who} asked Queravel about this {m.threadRoot ? "thread" : "chat"}
        </SystemLine>,
      );
      return;
    }

    const own = m.senderId === meId;
    const prev = messages[i - 1];
    const prevLine =
      prev?.kind === "system" || prev?.content?.type === "notice" || prev?.content?.type === "imported";
    const newRun = !prev || prevLine || prev.senderId !== m.senderId || dayKey(prev.createdAt) !== day;
    const spacing = newRun && prev && !prevLine ? (Object.keys(prev.reactions).length ? "mt-5" : "mt-2") : "";
    const sender = chat.members.find((x) => x.user.id === m.senderId)?.user;
    // Each message draws inside its own boundary, so one that fails shows a note and the rest carry on.
    const bubble = (
      <MessageBoundary key={m.id} own={own}>
        <RowBubble
          m={m}
          chat={chat}
          meId={meId}
          own={own}
          group={group}
          newRun={newRun}
          spacing={spacing}
          byId={byId}
          onRetry={onRetry}
          album={albums.get(m.id)}
          actionProps={actionProps}
          highlightId={highlightId}
          starred={starred}
          toolbar={toolbar}
          render={render}
        />
      </MessageBoundary>
    );

    if (group && !own) {
      items.push(
        <div key={m.id} className={spacing || "mt-1"}>
          <GroupedMessage
            avatar={
              newRun ? (
                <Avatar
                  name={sender?.display_name ?? "Someone"}
                  src={sender?.avatar_url}
                  tone={toneFor(m.senderId ?? "")}
                  size={28}
                />
              ) : undefined
            }
          >
            {bubble}
          </GroupedMessage>
        </div>,
      );
    } else if (group) {
      items.push(
        <div key={m.id} className={`flex flex-col ${spacing}`}>
          {bubble}
        </div>,
      );
    } else {
      items.push(<Fragment key={m.id}>{bubble}</Fragment>);
    }
    if (onThread && (m.replyCount ?? 0) > 0) {
      items.push(
        <ThreadSummary
          key={`${m.id}-thread`}
          m={m}
          own={own}
          indent={group && !own}
          onOpen={() => onThread(m)}
        />,
      );
    }
  });

  const typingNames = typing
    .filter((id) => id !== meId)
    .map((id) => firstName(nameIn(chat, id, meId)))
    .join(" and ");

  return (
    <>
      {items}
      {typingNames && <TypingBubble name={typingNames} />}
      <EncryptionSheet open={explained} onClose={() => setExplained(false)} onVerify={onNotice} />
    </>
  );
}

export function disappearLabel(seconds: number): string {
  if (seconds <= 86_400) return "24 hours";
  if (seconds <= 7 * 86_400) return "7 days";
  return "90 days";
}
